/* 夏侯霸（字仲權） */
import { SG, gd, spMul, sigOf } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, D } from '../define.ts';

const XIAHOU = ['xiahouyuan', 'xiahoudun'];

export default defineHero({
  id: 'xiahouba', name: '夏侯霸', sub: '字仲權', faction: 'wei', role: '射手', weapon: 'bow',
  look: { weaponName: '家傳鵲畫弓', beard: 'mustache', fx: '#9ad0ff', c: { plume: '#6fa8ff', cape: '#2b3f6e' } },
  skills: [
    ['家傳連矢', 'bolt', '扇形射出 3 箭，每箭 {45/68/90/112}；同一目標中 2 箭以上緩速 30%（1.5 秒）。', { x: { k: 'triarrow', dmg: [45, 68, 90, 112], fan: 0.13 }, aim: D(720, 60), range: 720, cd: 7, mp: [55, 65, 75, 85], el: null }],
    ['定軍山之仇', 'command', '標記一名敵將 8 秒為「仇敵」：對他的傷害 +{10/15/20/25}%；他在期間陣亡就重置家傳連矢與這招的冷卻。同隊夏侯淵陣亡時，自動把擊殺者標為仇敵（加成兩倍）。', { x: { k: 'vendetta', amp: [10, 15, 20, 25], dur: 8 }, aim: U(800), cd: [18, 16, 14, 12], mp: 60, el: null }],
    ['夏侯家風', 'passive', '同隊每有一名夏侯家的人（夏侯淵、夏侯惇），自己和他們攻速 +{5/8/11/14}%。', { pv: {}, sig: 'xhClan', as: [5, 8, 11, 14] }],
    ['穿陣一矢', 'bolt', '蓄力 1 秒，朝方向射出飛越全圖的長箭：命中第一名敵將 {250/375/500}＋他已損生命 15%，暈眩 1 秒；路上小兵受一半傷害。', { x: { k: 'longshot', dmg: [250, 375, 500], charge: 1 }, aim: D(6000, 40), cd: [90, 80, 70], mp: [120, 140, 160], el: null, shout: '父仇在此，一矢穿陣！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    triarrow(u, a, X) {
      const sp = spMul(u), hits = new Map<Unit, number>(), R = this.skillRange(a.sk, a.lvl);
      for (const j of [-1, 0, 1]) {
        const c = Math.cos(j * X.fan), s = Math.sin(j * X.fan), dir = [a.dir[0] * c - a.dir[1] * s, a.dir[0] * s + a.dir[1] * c];
        this.shootProj(u, { dir, speed: 1100, range: R, shape: 'arrow', col: u.spec.fx,
          filter: (t: Unit) => !t.structure,
          onHit: (t: Unit) => {
            this.damage(u, t, X.dmg * sp, { stagger: 0.15, proj: true });
            const n = (hits.get(t) || 0) + 1; hits.set(t, n);
            if (n === 2 && t.alive) this.addBuff(t, { id: 'tri', t: 1.5, ms: -0.3, neg: true });
            return true;
          } });
      }
    },
    vendetta(u, a, X) {
      const t = this.xPick(u, a.pt, 160);
      if (!t || t.kind !== 'hero' || t.team === u.team) { this.tell(u, '定軍山之仇：要指定敵將', 'warn'); this.xRefund(u, a); return; }
      this.markFoe(u, t, X.amp, X.dur);
    },
    longshot(u, a, X) {
      const dir = a.dir.slice(), sp = spMul(u);
      u.st.root = Math.max(u.st.root, X.charge);
      this.fx({ k: 'warn', x: u.x, y: u.y, r: 40, t: X.charge });
      this.later(X.charge, () => {
        if (!u.alive || u.st.stun > 0) return;
        this.shootProj(u, { dir, speed: 2400, range: 6000, rad: 22, big: true, shape: 'arrow', col: '#cfe6ff',
          filter: (t: Unit) => !t.structure,
          onHit: (t: Unit) => {
            if (t.kind !== 'hero') { this.damage(u, t, X.dmg * sp * 0.5, { proj: true }); return false; }
            this.damage(u, t, X.dmg * sp + (t.maxHp - t.hp) * 0.15, { proj: true, stagger: 0.5 });
            if (t.alive) this.applyCC(u, t, { stun: 1 });
            return true;
          } });
      });
    },
  },
  methods: {
    // 仇敵：被標記期間受到夏侯霸的傷害增加
    markFoe(u, t, amp, dur) {
      t.foeMark = { by: u, until: this.t + dur, amp };
      this.expose(t, u.team, dur);
      this.tell(u, `定軍山之仇：${t.name} 是仇敵`, 'info');
      this.fx({ k: 'warn', x: t.x, y: t.y, r: 50, t: 1 });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    hook('damage.before', 35, function (c) {
      const m = c.t.foeMark;
      if (m && m.by === c.own && m.until > this.t) c.a *= 1 + m.amp / 100;
    }),
    // 仇敵在標記期間陣亡：重置家傳連矢與定軍山之仇；同隊夏侯淵陣亡：自動標記兇手
    hook('kill.after', 30, function (src, t, was) {
      if (!was) return;
      const m = t.foeMark;
      if (m && m.until > this.t && m.by.alive) {
        const u = m.by; u.cd[0] = 0; u.cd[1] = 0; t.foeMark = null;
        this.tell(u, '定軍山之仇：仇敵已死，冷卻重置', 'info');
      }
      if (t.spec && t.spec.id === 'xiahouyuan') {
        const killer = src && (src.owner || src);
        for (const u of this.units) {
          if (!u.alive || u.team !== t.team || !u.spec || u.spec.id !== 'xiahouba' || !u.skillLv[1]) continue;
          if (killer && killer.kind === 'hero' && killer.alive && killer.team !== u.team) this.markFoe(u, killer, SG.L(u.spec.skills[1].x.amp, u.skillLv[1]) * 2, 8);
        }
      }
    }),
    // 夏侯家風：同隊夏侯家的人越多，彼此攻速越高
    hook('aura', 41, function () {
      for (const u of this.units) {
        const cl = u.alive && u.kind === 'hero' ? sigOf(u, 'xhClan') : null;
        if (!cl) continue;
        const kin = this.units.filter((o) => o.alive && o.kind === 'hero' && o.team === u.team && o.spec && XIAHOU.includes(o.spec.id));
        if (!kin.length) continue;
        const as = cl.v('as') * kin.length;
        for (const o of [u, ...kin]) this.addBuff(o, { id: 'xhClan', t: 0.7, as });
      }
    }),
    hook('revive', 71, function (u) { u.foeMark = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    triarrow: ({ t, d }) => !!t && d < 680,
    vendetta: ({ hero, d, t }) => hero && d < 760 && !!t && t.hp / t.maxHp < 0.8,
    longshot: ({ hero, t, d }) => hero && !!t && t.hp / t.maxHp < 0.45 && d < 1800,
  },
});
